import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { Edit2, Trash2, Plus, Check, X, Building, Hash, AlertTriangle } from 'lucide-react';
import { DEPARTMENT_CODE_LENGTH, cleanDepartmentCode, suggestDepartmentCode, formatMatricule } from '../utils/matricule';
import './Users.css';

const Departments = () => {
  const { isAdmin } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');
  // The code follows the name until the admin types one
  const [codeTyped, setCodeTyped] = useState(false);
  const [matriculeFormat, setMatriculeFormat] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCode, setEditCode] = useState('');

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [showForm, setShowForm] = useState(false);
  // Problems with the new department are shown inside its window
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);

  const openForm = () => {
    setName('');
    setDescription('');
    setCode('');
    setCodeTyped(false);
    setFormError('');
    setShowForm(true);
  };
  const closeForm = () => {
    if (!creating) setShowForm(false);
  };

  // Stats calculation
  const totalDepartments = departments.length;
  const totalAssignedUsers = departments.reduce((sum, d) => {
    const userCount = Array.isArray(d.users) ? d.users.length : (d.userCount || 0);
    return sum + userCount;
  }, 0);

  const fetchDepartments = useCallback(async () => {
    try {
      const data = await api.get('/departments');
      setDepartments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    api.get('/matricule/format').then(setMatriculeFormat).catch(() => setMatriculeFormat(null));
  }, [fetchDepartments]);

  const codeIsComplete = (value) => value.length === DEPARTMENT_CODE_LENGTH;
  const codeOwner = (value, exceptId) => departments.find((d) => d.code === value && d.id !== exceptId);
  const missingCodes = departments.filter((d) => !d.code);
  const exampleYear = new Date().getFullYear();
  const exampleMatricule = (deptCode, isIntern) => formatMatricule({
    format: matriculeFormat,
    code: deptCode,
    isIntern,
    year: exampleYear,
    number: (isIntern ? matriculeFormat?.nextInternNumber : matriculeFormat?.nextStaffNumber) || 1,
  });

  const createDepartment = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setFormError('');
    if (!codeIsComplete(code)) {
      setFormError(`The matricule code must be ${DEPARTMENT_CODE_LENGTH} letters (for example QM).`);
      return;
    }
    if (codeOwner(code)) {
      setFormError(`The code ${code} is already used by "${codeOwner(code).name}". Choose another one.`);
      return;
    }
    setCreating(true);
    try {
      await api.post('/departments', { name: name.trim(), code, description: description.trim() || undefined });
      setShowForm(false);
      setMessage({ type: 'success', text: `Department "${name.trim()}" created successfully!` });
      fetchDepartments();
    } catch (error) {
      setFormError(error.message || 'Failed to create department');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (dept) => {
    setEditingId(dept.id);
    setEditName(dept.name);
    setEditDescription(dept.description || '');
    setEditCode(dept.code || '');
    setMessage({ type: '', text: '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditCode('');
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    if (!codeIsComplete(editCode)) {
      setMessage({ type: 'error', text: `The matricule code must be ${DEPARTMENT_CODE_LENGTH} letters (for example QM).` });
      return;
    }
    if (codeOwner(editCode, id)) {
      setMessage({ type: 'error', text: `The code ${editCode} is already used by "${codeOwner(editCode, id).name}". Choose another one.` });
      return;
    }
    try {
      await api.patch(`/departments/${id}`, {
        name: editName.trim(),
        code: editCode,
        description: editDescription.trim() || undefined,
      });
      setEditingId(null);
      setMessage({ type: 'success', text: 'Department updated successfully!' });
      fetchDepartments();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update department' });
    }
  };

  const promptDelete = (dept) => {
    setDeptToDelete(dept);
    setShowDeleteModal(true);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    if (!deptToDelete) return;
    try {
      await api.delete(`/departments/${deptToDelete.id}`);
      setMessage({ type: 'success', text: `Department "${deptToDelete.name}" deleted successfully!` });
      fetchDepartments();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete department' });
    } finally {
      setShowDeleteModal(false);
      setDeptToDelete(null);
    }
  };

  if (loading) return <div className="page-loading">Loading departments...</div>;

  return (
    <div className="users-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1><Building size={24} /> Departments Management</h1>
        {isAdmin() && (
          <button className="btn btn-primary" onClick={openForm} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} /> New Department
          </button>
        )}
      </div>

      <div className="tr-stats-grid" style={{ marginBottom: 16 }}>
        <div className="tr-stat-card">
          <div className="tr-stat-label">Total Departments</div>
          <div className="tr-stat-value">{totalDepartments}</div>
        </div>
        <div className="tr-stat-card">
          <div className="tr-stat-label">Total Assigned Employees</div>
          <div className="tr-stat-value">{totalAssignedUsers}</div>
        </div>
      </div>

      <div className="tr-card" style={{ marginBottom: 20, padding: 20, border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
          <Hash size={18} style={{ color: '#6366f1' }} /> Matricule format
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          Every new user receives a matricule automatically. Only the department code can be changed, in the
          table below; the other parts are fixed.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          {[
            { title: 'Employees, managers and admins', isIntern: false },
            { title: 'Interns', isIntern: true },
          ].map(({ title, isIntern }) => (
            <div key={title} style={{ padding: 14, borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>{title}</div>
              <code style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
                {exampleMatricule(departments.find((d) => d.code)?.code || 'QM', isIntern)}
              </code>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                Next {isIntern ? 'intern' : 'employee'} number: {(isIntern ? matriculeFormat?.nextInternNumber : matriculeFormat?.nextStaffNumber) || 1}
              </div>
            </div>
          ))}
        </div>
        <ul style={{ margin: '14px 0 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)' }}>
          <li><strong>PR</strong> — the company, always the same{' '}(interns also carry the word <strong>INTERN</strong>)</li>
          <li><strong>2 letters</strong> — the code of the user's department</li>
          <li><strong>2 digits</strong> — the year the user joined</li>
          <li><strong>5 digits</strong> — a number that goes up by one for each new user, whatever the department. Employees and interns are counted separately.</li>
        </ul>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
          Changing a department code only affects the users created afterwards: existing matricules never change.
        </p>
        {missingCodes.length > 0 && (
          <div className="message error" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} />
            <span>
              No matricule code yet for: {missingCodes.map((d) => d.name).join(', ')}. Users cannot be added to
              {missingCodes.length === 1 ? ' this department' : ' these departments'} until you set it (pencil button).
            </span>
          </div>
        )}
      </div>

      {message.text && (
        <div className={`message ${message.type}`} style={{ marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      {isAdmin() && showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-department-title"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: 24, borderRadius: 12, border: '1px solid var(--border-color)', background: 'var(--bg-card)', boxShadow: '0 20px 40px rgba(0, 0, 0, 0.35)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 id="new-department-title" style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
                <Building size={20} style={{ color: '#6366f1' }} /> New Department
              </h3>
              <button
                type="button"
                onClick={closeForm}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4 }}
                title="Close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {formError && (
              <div className="message error" style={{ marginBottom: 14 }}>{formError}</div>
            )}

            <form onSubmit={createDepartment} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label htmlFor="new-department-name" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Department Name *</label>
                <input
                  id="new-department-name"
                  placeholder="e.g., Engineering, Human Resources, Sales"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!codeTyped) setCode(suggestDepartmentCode(e.target.value));
                  }}
                  className="tr-input"
                  style={{ width: '100%' }}
                  maxLength={100}
                  autoFocus
                  required
                />
              </div>

              <div>
                <label htmlFor="new-department-code" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Matricule code *</label>
                <input
                  id="new-department-code"
                  placeholder="e.g., EN"
                  value={code}
                  onChange={(e) => {
                    setCode(cleanDepartmentCode(e.target.value));
                    setCodeTyped(true);
                  }}
                  className="tr-input"
                  style={{ width: 110, textTransform: 'uppercase', letterSpacing: 2 }}
                  maxLength={DEPARTMENT_CODE_LENGTH}
                  required
                />
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                  {codeIsComplete(code)
                    ? <>Matricules of this department will look like <code>{exampleMatricule(code, false)}</code></>
                    : '2 letters, proposed from the name. They appear in the matricule of the users of this department.'}
                </div>
              </div>

              <div>
                <label htmlFor="new-department-description" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Description (optional)</label>
                <input
                  id="new-department-description"
                  placeholder="Short summary of department responsibilities"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="tr-input"
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button className="btn btn-secondary" type="button" onClick={closeForm} disabled={creating}>
                  Cancel
                </button>
                <button className="btn btn-primary" type="submit" disabled={creating} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Plus size={16} /> {creating ? 'Creating…' : 'Create Department'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Matricule code</th>
              <th>Description</th>
              <th>Assigned Users</th>
              {isAdmin() && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {departments.length ? (
              departments.map((d) => {
                const isEditing = editingId === d.id;
                const userCount = Array.isArray(d.users) ? d.users.length : (d.userCount || 0);

                return (
                  <tr key={d.id}>
                    <td>#{d.id}</td>
                    <td>
                      {isEditing ? (
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="tr-input"
                          style={{ padding: '6px 10px', width: '100%' }}
                          autoFocus
                        />
                      ) : (
                        <strong>{d.name}</strong>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          value={editCode}
                          onChange={(e) => setEditCode(cleanDepartmentCode(e.target.value))}
                          className="tr-input"
                          style={{ padding: '6px 10px', width: 70, textTransform: 'uppercase', letterSpacing: 2 }}
                          maxLength={DEPARTMENT_CODE_LENGTH}
                          placeholder="QM"
                          title="2 letters used in the matricule of this department's users"
                        />
                      ) : d.code ? (
                        <div>
                          <code style={{ fontWeight: 700 }}>{d.code}</code>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                            e.g. {exampleMatricule(d.code, false)}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 600 }}>Not set</span>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className="tr-input"
                          style={{ padding: '6px 10px', width: '100%' }}
                        />
                      ) : (
                        d.description || '-'
                      )}
                    </td>
                    <td>
                      <div>
                        <span className="badge-count" style={{ padding: '3px 10px', borderRadius: '12px', background: 'rgba(99,102,241,0.15)', color: '#6366f1', fontSize: 12, fontWeight: 700, display: 'inline-block', marginBottom: Array.isArray(d.users) && d.users.length > 0 ? 4 : 0 }}>
                          {userCount} {userCount === 1 ? 'User' : 'Users'}
                        </span>
                        {Array.isArray(d.users) && d.users.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {d.users.map((u) => (
                              <span key={u.id} style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 4, color: 'var(--text-primary)' }}>
                                {u.firstName} {u.lastName}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    {isAdmin() && (
                      <td className="actions-cell">
                        <div className="actions" style={{ display: 'flex', gap: 6 }}>
                          {isEditing ? (
                            <>
                              <button
                                className="btn-icon"
                                title="Save changes"
                                onClick={() => saveEdit(d.id)}
                                style={{ color: '#10b981' }}
                              >
                                <Check size={16} />
                              </button>
                              <button
                                className="btn-icon"
                                title="Cancel edit"
                                onClick={cancelEdit}
                                style={{ color: '#ef4444' }}
                              >
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn-icon"
                                title="Edit department"
                                onClick={() => startEdit(d)}
                              >
                                <Edit2 size={15} />
                              </button>
                              <button
                                className="btn-icon delete"
                                title="Delete department"
                                onClick={() => promptDelete(d)}
                              >
                                <Trash2 size={15} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan={isAdmin() ? "6" : "5"} style={{ textAlign: 'center', color: 'var(--text2)', padding: 30 }}>No departments found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Department"
        message={
          deptToDelete && Array.isArray(deptToDelete.users) && deptToDelete.users.length > 0
            ? `Department "${deptToDelete?.name}" has ${deptToDelete.users.length} active users assigned to it. Deleting it will set their department to unassigned. Are you sure?`
            : `Are you sure you want to delete the department "${deptToDelete?.name}"?`
        }
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeptToDelete(null);
        }}
        confirmText="Delete Department"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Departments;
